import { useState } from 'react';
import { Flex, Box, Text } from '@keyslabor/shared/ui';
import { Input } from '@keyslabor/shared/ui/Input';
import { useNavigate } from 'react-router-dom';
import ManagerLogo from '@keyslabor/shared/assets/svgComponents/ManagerLogo';
import useAjax from '@/hooks/useAjax';
import { ajaxRoutes } from '@/globalConstants';
import PropertiesDownIcon from '@keyslabor/shared/assets/svgComponents/PropertiesDownIcon';
import { useCompanyInfoQuery } from '@/api/methods/useCompanyInfoQuery';
import { usePropertiesQuery } from '@/hooks/api/useProperties.ts';
import { Option } from '@/globalTypes.ts';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { cn } from '@/lib/utils.ts';
import { Separator } from '@/components/ui/separator.tsx';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

export default function PropertySelect() {
  const { data: companyInfo } = useCompanyInfoQuery();

  const [searchQuery, setSearchQuery] = useState<string>('');
  const navigate = useNavigate();

  const [properties] = usePropertiesQuery();

  const [triggerSelectProperty] = useAjax({
    url: ajaxRoutes.property,
    type: 'POST',
    onSuccess: () => {
      navigate(0);
    },
  });

  const handleSearch = (options: Option[] | null, searchQuery: string) => {
    if (!options) return [];

    return options.filter((item) => {
      return item?.label?.toLowerCase().includes(searchQuery.toLowerCase());
    });
  };

  const handleChooseProperty = (id: Option['value']) => {
    triggerSelectProperty({
      fields: {
        properties: id,
      },
    });
  };

  const activeProperty: Option | undefined = properties?.find(
    ({ value }) => value === companyInfo?.property?.id
  );

  const hasLength = properties ? properties.length > 1 : false;

  return (
    <div className="flex relative h-10 w-auto md:w-[256px] 2xl:w-[324px] items-center mr-4 lg:mr-0">
      <Dialog>
        <DialogTrigger className="group w-full">
          <div
            className="flex gap-2 w-full h-10 items-center rounded cursor-pointer py-1 pr-2"
            role="button"
          >
            <ManagerLogo />
            <div className="flex flex-col flex-1 text-left">
              <div
                className={`font-black text-[#143980] truncate uppercase text-xs max-w-[190px] lg:text-sm lg:max-w-[255px]  leading-none`}
                title={activeProperty?.label}
              >
                {activeProperty?.label}
              </div>
              <div
                className="font-black text-[#143980] truncate text-sm max-w-[255px] uppercase hidden lg:block  leading-none"
                title={companyInfo?.manager?.name}
              >
                {companyInfo?.manager?.name}
              </div>
            </div>
            {hasLength && (
              <div
                className={cn(
                  'mr-2 duration-300',
                  'rotate-180 group-data-[state=open]:rotate-0'
                )}
              >
                <PropertiesDownIcon />
              </div>
            )}
            <Separator
              orientation="vertical"
              className="ml-auto hidden lg:block bg-[#BFC3DB] w-[2px]"
            />
          </div>
        </DialogTrigger>
        <DialogContent variant={'modal'} className="rounded">
          <VisuallyHidden>
            <DialogTitle>Select Property</DialogTitle>
          </VisuallyHidden>
          <Box p="0px 8px">
            <Input
              value={searchQuery}
              name="test"
              placeholder="Search property"
              height="30px"
              fontSize="10px"
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              borderRadius="20px"
              borderColor="#B8CAD5"
            />
          </Box>
          <Flex
            // width="100%"
            height="260px"
            flexDirection="column"
            mt="12px"
            p="0px 20px"
            overflowY="scroll"
          >
            {handleSearch(properties, searchQuery)?.map(({ value, label }) => {
              return (
                <Box
                  key={value}
                  width="100%"
                  height="30px"
                  mt="16px"
                  css={`
                    cursor: pointer;
                    text-transform: uppercase;
                    &:nth-child(1) {
                      margin-top: 0px;
                    }
                  `}
                >
                  <Text
                    fontWeight="400"
                    fontSize="12px"
                    onClick={() => handleChooseProperty(value)}
                    noWrap={true}
                    maxWidth="280px"
                    maxChars={120}
                  >
                    {label}
                  </Text>
                </Box>
              );
            })}
          </Flex>
        </DialogContent>
      </Dialog>
    </div>
  );
}
